import { Injectable } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';
import type { PieChartConfig } from '../../../shared/echarts/models/chart-types';
import type { DashboardFilterOptions } from '../models/dashboard-filters.model';
import type { MatrixAnalyticsRow, MatrixAnalyticsStatus, MatrixAnalyticsTrend } from '../models/matrix-analytics.model';
import type { MatrixInvestigationRow } from '../models/matrix-investigation.model';
import { MatrixTrackerCardData } from '../models/matrix-tracker-chart';
import type { DashboardDataService } from './dashboard-data.token';

export interface TableDataResponse<T> {
  readonly data: readonly T[];
  readonly totalRecords: number;
}

export interface MidasAiVideosCardData {
  readonly flagged: number;
  readonly notFlagged: number;
  readonly periodLabel: string;
  readonly chartConfig: PieChartConfig;
}

@Injectable({ providedIn: 'root' })
export class DashboardMockService implements DashboardDataService {
  private readonly filters: DashboardFilterOptions = {
    favorites: [
      { label: 'Filter Favorites', value: 'favorites' },
      { label: 'My Default', value: 'default' },
      { label: 'Last Used', value: 'last' },
    ],
    medications: [
      { label: 'Select Medi', value: '' },
      { label: 'Medication A', value: 'med_a' },
      { label: 'Medication B', value: 'med_b' },
    ],
    clinicalUnits: [
      { label: 'Select Unit', value: '' },
      { label: 'Unit 1', value: 'unit_1' },
      { label: 'Unit 2', value: 'unit_2' },
    ],
    serviceDepts: [
      { label: 'Select Depa', value: '' },
      { label: 'Service Dept 1', value: 'svc_1' },
      { label: 'Service Dept 2', value: 'svc_2' },
    ],
  };

  private readonly analyticsRows: readonly MatrixAnalyticsRow[] = generateAnalyticsRows(40);
  private readonly investigationRows: readonly MatrixInvestigationRow[] = generateInvestigationRows(30);

  getFilterOptions(): Observable<DashboardFilterOptions> {
    return of(this.filters).pipe(delay(150));
  }

  getMatrixAnalytics(query: TableQuery): Observable<TableDataResponse<MatrixAnalyticsRow>> {
    return defer(() => {
      const normalized = normalizeQuery(query);

      const filtered = applySearch(this.analyticsRows, normalized.search, (row) => [
        row.employeeName,
        row.jobRole,
        row.status,
        String(row.riskScore),
      ]);

      const sorted = applySort(filtered, normalized.sortField, normalized.sortOrder);

      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<MatrixAnalyticsRow>);
    }).pipe(delay(250));
  }

  getPatientInvestigation(query: TableQuery): Observable<MatrixInvestigationRow[]> {
    return defer(() => {
      const normalized = normalizeQuery(query);

      const filtered = applySearch(this.investigationRows, normalized.search, (row) => [
        row.investigatorName,
        row.caseDate,
        row.caseTime,
        row.managedEmployee,
        row.employeeStatus,
      ]);

      const sorted = applySort(filtered, normalized.sortField, normalized.sortOrder);

      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of([...pageData]);
    }).pipe(delay(250));
  }

  private trackerCardCache: MatrixTrackerCardData | null = null;

  getMatrixTrackerCardData(params?: { organizationId: string; daysBack: string }): Observable<MatrixTrackerCardData> {
    if (this.trackerCardCache) {
      return of(this.trackerCardCache);
    }

    const reviewed = 1600;
    const totalVariances = 2000;
    const outstanding = 100;
    const sentToInvestigation = 300;

    const chartConfig: PieChartConfig = {
      type: 'pie',
      series: [
        {
          name: 'Variances',
          data: [
            {
              name: 'Reviewed Variance',
              value: sentToInvestigation,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#88D3EE' }, // lighter cyan
                    { offset: 1, color: '#2C87BD' }, // #88d3ee
                  ],
                },
              },
            },
            {
              name: 'Open Variance',
              value: outstanding,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#fbbf24' }, // lighter orange
                    { offset: 1, color: '#f59e0b' }, // #f59e0b
                  ],
                },
              },
            },
            {
              name: 'New Variance',
              value: reviewed,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#a3e635' }, // lighter lime
                    { offset: 1, color: '#84cc16' }, // #84cc16
                  ],
                },
              },
            },
          ],
          radius: ['60%', '95%'],
          label: { show: false },
          emphasis: { scale: true },
        },
      ],
      tooltip: { trigger: 'item' },
    };

    const daysBack = params?.daysBack || '180';
    this.trackerCardCache = {
      reviewed,
      outstanding,
      sentToInvestigation,
      totalVariances,
      periodLabel: `Last ${daysBack} Days`,
      chartConfig,
    } satisfies MatrixTrackerCardData;
    return of(this.trackerCardCache).pipe(delay(150));
  }

  getMidasAiVideosCardData(): Observable<MidasAiVideosCardData> {
    const flagged = 28;
    const notFlagged = 72;

    const chartConfig: PieChartConfig = {
      type: 'pie',
      series: [
        {
          name: 'AI Videos',
          data: [
            {
              name: 'Flagged',
              value: flagged,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#60c5f0' }, // lighter blue
                    { offset: 1, color: '#39a8de' }, // #39a8de
                  ],
                },
              },
            },
            {
              name: 'Not flagged',
              value: notFlagged,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#fbbf24' }, // lighter orange
                    { offset: 1, color: '#f59e0b' }, // #f59e0b
                  ],
                },
              },
            },
          ],
          radius: ['50%', '75%'],
          label: { show: false },
          emphasis: { scale: true },
        },
      ],
      tooltip: { trigger: 'item' },
    };

    return of({
      flagged,
      notFlagged,
      periodLabel: 'Last 30 Days',
      chartConfig,
    } satisfies MidasAiVideosCardData).pipe(delay(150));
  }
}

function normalizeQuery(
  q: TableQuery
): Required<Pick<TableQuery, 'page' | 'rows'>> & TableQuery {
  return {
    ...q,
    page: Math.max(0, q.page ?? 0),
    rows: Math.max(1, q.rows ?? 10),
  };
}

function applySearch<T>(
  rows: readonly T[],
  search: string | undefined,
  fields: (row: T) => readonly string[]
): T[] {
  const term = (search ?? '').trim().toLowerCase();
  if (!term) return [...rows];

  return rows.filter((r) => fields(r).some((v) => v.toLowerCase().includes(term)));
}

function applySort<T extends Record<string, unknown>>(
  rows: T[],
  sortField: string | undefined,
  sortOrder: 1 | -1 | undefined
): T[] {
  if (!sortField) return rows;
  const order: 1 | -1 = sortOrder === -1 ? -1 : 1;

  return [...rows].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];

    if (av == null && bv == null) return 0;
    if (av == null) return -1 * order;
    if (bv == null) return 1 * order;

    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;

    return String(av).localeCompare(String(bv)) * order;
  });
}

function generateAnalyticsRows(count: number): MatrixAnalyticsRow[] {
  const names = [
    'Ahmad Ekstrom',
    'Davis Franci',
    'Dulce Rhiel Madsen',
    'Talan Passaquindici',
    'Ahmad Geidt',
    'Mateo Jackson',
    'Scarlett Green',
    'Elijah Thompson',
    'James Turner',
    'Avery White',
  ] as const;

  const roles: readonly MatrixAnalyticsRow['jobRole'][] = ['CRNA', 'Nurse', 'Pharm Tec', 'Pharmacist'];
  const trends: readonly MatrixAnalyticsTrend[] = ["up", "down", "flat", "trending_up", "trending_down"];
  const statuses: readonly string[] = ['Requires Review', 'Under Invest', 'Referral', 'In Progress'];

  return Array.from({ length: count }, (_, i) => ({
    guid: String(1000 + i),
    employeeName: names[i % names.length],
    jobRole: roles[i % roles.length],
    trend7dDirection: trends[i % trends.length],
    riskScore: (i % 5) + 1,
    status: statuses[i % statuses.length],
  }));
}

function generateInvestigationRows(count: number): MatrixInvestigationRow[] {
  const investigators = [
    'Ahmad Ekstrom',
    'Davis Franci',
    'Dulce Rhiel Madsen',
    'Talan Passaquindici',
    'Ahmad Geidt',
  ] as const;

  const employees = ['Mateo Jackson', 'Scarlett Green', 'Elijah Thompson', 'James Turner', 'Avery White'] as const;

  const statuses = [
    'In Progress',
    'UDS Results Pending',
    'Referral',
    'Requires Review',
  ] as const;

  const dates = ['07/04/2024', '05/01/2024', '06/15/2024', '03/04/2024', '08/22/2024'] as const;
  const times = ['02:00 pm', '04:30 am', '02:34 am', '04:43 pm', '02:00 pm'] as const;

  return Array.from({ length: count }, (_, i) => ({
    investigatorName: investigators[i % investigators.length],
    caseDate: dates[i % dates.length],
    caseTime: times[i % times.length],
    managedEmployee: employees[i % employees.length],
    employeeStatus: statuses[i % statuses.length],
  }));
}


